// Scripted command feed for the homepage Operate panel — the same shape
// GET /apps/:org/:app/command returns (shared/commandFeed.js), so the real
// CommandScene + deriveCommand draw it unchanged. Fixture values except the
// remediation timing, which is the measured one (measurements.gen.json
// fleet.restartMs / consecutiveReports): the api service is reported down
// twice, the fleet restarts it, and the row lands in the audit.
import MEASURED from './measurements.gen.json';

const DAY = 86400e3;
const NOW = Date.UTC(2026, 9, 13, 18, 0, 0); // a fixed "now" so the picture is deterministic

export const RESTART_SEC = MEASURED.fleet.restartMs / 1000;
export const DOWN_REPORTS = MEASURED.fleet.consecutiveReports;
export const RESTART_SOURCE = MEASURED.fleet.source;

const service = (name, kind = 'service', state = 'running', health = 'healthy') => ({ name, kind, state, health, status: state });

// `phase`: 'calm' | 'down' | 'restarting' | 'healed'
export default function commandDemo(phase = 'calm') {
  const apiDown = phase === 'down' || phase === 'restarting';
  const services = [
    service('edge', 'service'),
    service('api', 'service', apiDown ? 'exited' : 'running', phase === 'restarting' ? 'starting' : 'healthy'),
    service('workers', 'service'),
    service('db', 'service'),
    service('storage', 'service'),
    service('mail', 'service'),
    service('runner', 'runner', 'exited', 'healthy'),
  ];
  if (phase === 'restarting') { services[1].state = 'running'; services[1].health = 'starting'; }
  const incidents = apiDown ? [{ id: 'inc-1', kind: 'probe', status: 'open', openedAt: NOW - 20e3, title: 'api reported down' }] : [];
  const activity = [
    ...(phase === 'healed' ? [{ at: NOW, kind: 'instance.selfHeal', text: `api restarted by the fleet in ${RESTART_SEC.toFixed(1)} s · on the record` }] : []),
    { at: NOW - 3600e3 * 5, kind: 'deploy', text: 'prod deploy done · health-gated' },
    { at: NOW - DAY, kind: 'scan', text: 'assurance baseline · 0 critical' },
  ];
  return {
    instance: { name: 'northline', slug: 'northline', status: 'live', url: 'https://northline.apps.nventr.io', kitRef: '7768df8', version: '2026.10.2', assurance: true },
    plan: 'kit',
    pinnedKitRef: '7768df8',
    license: { state: 'valid', status: 'valid', lastSeenAt: NOW - 30e3 },
    plane: {
      instance: { name: 'northline', slug: 'northline', status: 'live', url: 'https://northline.apps.nventr.io', kitRef: '7768df8', assurance: true },
      monitor: { state: apiDown ? 'down' : 'up', lastCheckAt: NOW - 12e3, lastLatencyMs: phase === 'healed' ? 212 : 188, consecFails: apiDown ? DOWN_REPORTS : 0, tlsDaysLeft: 71 },
      incidents,
      scan: { counts: { critical: 0, high: 0, medium: 2, low: 5, info: 9 }, lastScanAt: NOW - DAY * 3, running: false },
      twin: { exists: true, status: 'live', lastDeploy: { status: 'done', at: NOW - DAY * 2, env: 'uat' }, monitor: { state: 'up' } },
      prod: { lastDeploy: { status: 'done', at: NOW - 3600e3 * 5, env: 'prod' } },
      edge: { domains: [{ host: 'northline.apps.nventr.io', label: 'prod', tlsDaysLeft: 71 }, { host: 'northline-uat.apps.nventr.io', label: 'uat', tlsDaysLeft: 71 }] },
      updates: { versionBehind: false },
      activity,
    },
    services,
    costs: { current: { mtdUsd: 41.8, projectedUsd: 97.6, burnPerDayUsd: 3.2, infra: { usd: 27.1 }, model: { usd: 11.4 }, dev: { usd: 3.3 } }, prior: { mtdUsd: 36.2 }, priorPeriod: '2026-09' },
    limits: { app: 150 },
    health: {
      days: Array.from({ length: 14 }, (_, i) => ({ day: new Date(NOW - (13 - i) * DAY).toISOString().slice(0, 10), uptime: i === 5 ? 0.9971 : 1 })),
      uptime14d: 0.9998, p50: 96, p95: phase === 'healed' ? 212 : 188,
      features: { total: 6, passing: apiDown ? 5 : 6, failing: apiDown ? ['api'] : [] },
    },
    usage: {
      counters: { tokens: 1_840_000, aiTurns: 2140, pipelineRuns: 388, jobRuns: 61, agentConversations: 412, devCents: 330, voiceMinutes: 12 },
      byUser: [{ uid: 'u1', name: 'ops lead', tokens: 760_000 }, { uid: 'u2', name: 'controller', tokens: 520_000 }, { uid: 'u3', name: 'engineer', tokens: 310_000 }],
      reportedAt: NOW - 60e3, lastActivityAt: NOW - 90e3,
    },
    recentDeploys: [
      { env: 'prod', status: 'done', at: NOW - 3600e3 * 5, headSha: 'c41e9a2' },
      { env: 'uat', status: 'done', at: NOW - DAY * 2, headSha: 'c41e9a2' },
      { env: 'prod', status: 'done', at: NOW - DAY * 4, headSha: 'b90d1f7' },
      { env: 'uat', status: 'done', at: NOW - DAY * 5, headSha: 'b90d1f7' },
      { env: 'uat', status: 'error', at: NOW - DAY * 6, headSha: '8a7c3e1' },
    ],
    host: { id: 'host-2', load: { memMb: 2890, cpuPct: 21 }, memShare: 0.18 },
    scanSchedule: { nextBaselineAt: NOW + DAY * 27 },
    period: '2026-10',
    // The app's surfaces (doors on the chassis), the other apps on the host,
    // and a live-wire sample for the rooms' fixtures (`fixture: true` makes
    // the stage cycle the rows at a trickle instead of waiting for Firestore).
    surfaces: { mcp: 'up', mail: { status: 'live', domain: 'mail.northline.app', liveAt: NOW - DAY * 9 }, backup: { lastAt: NOW - 3600e3 * 7, ok: true, bytes: 1.24e9 }, native: null, devConsole: true, realtime: 'kit', voice: true },
    neighbours: [{ reqPerMin: 38, s5xx: 0, memMb: 610, uat: false }, { reqPerMin: 4, s5xx: 1, memMb: 420, uat: false }, { reqPerMin: 0, s5xx: 0, memMb: 380, uat: true }],
    live: {
      fixture: true, at: Date.now() - 8000,
      ev: [
        { t: NOW, m: 'GET', r: '/', s: 200, ms: 41 }, { t: NOW, m: 'POST', r: '/graphql', s: 200, ms: 88 }, { t: NOW, m: 'GET', r: '/rest/items?select=*', s: 200, ms: 23 },
        { t: NOW, m: 'POST', r: '/mcp', s: 200, ms: 140 }, { t: NOW, m: 'GET', r: '/auth/v1/user', s: 200, ms: 19 }, { t: NOW, m: 'GET', r: '/storage/v1/object/public/a.png', s: 200, ms: 12 },
        { t: NOW, m: 'POST', r: '/agent/chat', s: 200, ms: 1210 }, { t: NOW, m: 'GET', r: '/rest/items/42', s: 404, ms: 9 }, { t: NOW, m: 'GET', r: '/realtime/v1/websocket', s: 101, ms: null },
        { t: NOW, m: 'GET', r: '/dev/health', s: 200, ms: 7 }, { t: NOW, m: 'POST', r: '/voice/session', s: 200, ms: 320 }, { t: NOW, m: 'GET', r: '/pricing', s: 200, ms: 33 },
        ...(apiDown ? [{ t: NOW, m: 'GET', r: '/rest/items', s: 502, ms: 2 }, { t: NOW, m: 'POST', r: '/graphql', s: 502, ms: 3 }] : []),
      ],
      c: { at: Date.now() - 8000, by: { edge: { cpu: 1.8, memMb: 46, memPct: 2 }, api: { cpu: apiDown ? 0 : 17.2, memMb: 412, memPct: 40 }, workers: { cpu: 6.1, memMb: 188, memPct: 18 }, db: { cpu: 3.4, memMb: 262, memPct: 26 }, storage: { cpu: 0.6, memMb: 72, memPct: 7 }, mail: { cpu: 0.2, memMb: 58, memPct: 6 } } },
    },
  };
}
